import type { Block } from '@w/content-blocks'
import type { Change, Doc } from '@w/crdt'
import { processImage, toAac } from '@w/media-processing'
import type { UFile } from '@w/utils'
import { fileUploadFailedError } from './errors'
import { getAffectedBlocks, pageDocument } from './doc-bindings-for-page'
import type { FileHandler } from './types'
import type { MediaType } from './types'
import {
  getMediaCdcChanges,
  getPageRelationCdcChanges,
  getQuestionCdcChanges,
  type PageCdcChange,
  type PageRelationCdcState,
} from './question-cdc'

export interface BucketCdcChange {
  change: Change
  before: Block[]
  after: Block[]
  affected: ReturnType<typeof getAffectedBlocks>
  changes: PageCdcChange[]
}

export interface BucketCdcFiles {
  getUrl: (path: string) => string
  put: (file: UFile) => Promise<{ size: number }>
  delete: (path: string, opts?: { soft?: boolean }) => Promise<void>
  restore: (path: string) => Promise<void>
}

export interface BucketCdcOptions {
  doc: Doc
  pageId: string
  workspaceId: string
  assetDir?: string
  files: BucketCdcFiles
  onChange: (change: Change, effects: BucketCdcChange) => void
}

export interface BucketCdc {
  fileHandler: FileHandler
  onChange: (change: Change) => void
  sync: (opts: Partial<Omit<BucketCdcOptions, 'files'>>) => void
}

interface PreparedUpload {
  file: UFile
  mediaType: MediaType
  $file: string
  name?: string
  size?: number
  cleanup?: () => void
}


export function createBucketCdc(opts: BucketCdcOptions): BucketCdc {
  const files = opts.files
  const state = {
    doc: opts.doc,
    pageId: opts.pageId,
    workspaceId: opts.workspaceId,
    assetDir: opts.assetDir,
    onChange: opts.onChange,
    pageRelations: new Map() as PageRelationCdcState,
  }

  const fileHandler: FileHandler = {
    getUrl: (path) => files.getUrl(path),
    upload: async (inputFiles) => {
      const assetDir =
        state.assetDir ?? `assets/${state.workspaceId}/${state.pageId}`
      const prepared = await Promise.all(
        inputFiles.map((file) => _prepareUpload(file, assetDir)),
      )

      const items = prepared.map((item) => ({
        mediaType: item.mediaType,
        src: item.file.path,
        $file: item.$file,
        name: item.name,
        size: item.size,
        cleanup: item.cleanup,
      }))

      const done = Promise.all(
        prepared.map(async ({ file }) => {
          await files.put(file)
          return file.path
        }),
      )
        .then((srcs) => ({ srcs }))
        .catch((error) => {
          throw fileUploadFailedError(_toError(error))
        })

      return { items, done }
    },
  }

  return {
    fileHandler,
    onChange(change) {
      if (!change.changed) return
      const before = pageDocument({ toJSON: () => change.delta.before }).content.children as unknown as Block[]
      const after = pageDocument({ toJSON: () => change.delta.after }).content.children as unknown as Block[]
      const affected = getAffectedBlocks(change.delta)
      state.onChange(change, { change, before, after, affected, changes: [
        ...getPageRelationCdcChanges({ delta: change.delta, workspaceId: state.workspaceId, pageId: state.pageId, state: state.pageRelations }),
        ...getQuestionCdcChanges(change.delta), ...getMediaCdcChanges(change.delta),
      ] })
    },
    sync(next) {
      if (next.doc !== undefined) state.doc = next.doc
      if (next.pageId !== undefined && next.pageId !== state.pageId) {
        state.pageId = next.pageId
        state.pageRelations.clear()
      }
      if (
        next.workspaceId !== undefined &&
        next.workspaceId !== state.workspaceId
      ) {
        state.workspaceId = next.workspaceId
        state.pageRelations.clear()
      }
      if (next.assetDir !== undefined) state.assetDir = next.assetDir
      if (next.onChange !== undefined) state.onChange = next.onChange
    },
  }
}

async function _prepareUpload(
  inputFile: UFile,
  assetDir: string,
): Promise<PreparedUpload> {
  const file = inputFile.moveTo(assetDir).makeNameUnique()

  if (file.isImage) {
    const preview = file.createObjectURL()
    const result = await processImage(file)
    return {
      file: result.file,
      mediaType: 'img',
      $file: preview,
      cleanup: () => {
        URL.revokeObjectURL(preview)
      },
    }
  }

  if (file.isAudio) {
    const preview = file.createObjectURL()
    const converted = await toAac(file)
    return {
      file: converted,
      mediaType: 'audio',
      $file: preview,
      cleanup: () => {
        URL.revokeObjectURL(preview)
      },
    }
  }

  if (file.isVideo) {
    const preview = file.createObjectURL()
    return {
      file,
      mediaType: 'video',
      $file: preview,
      cleanup: () => {
        URL.revokeObjectURL(preview)
      },
    }
  }

  return {
    file,
    mediaType: 'file',
    $file: '',
    name: file.name,
    size: file.size,
  }
}

function _toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}
